import { useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import {
  Shield, ShieldAlert, ShieldOff, ShieldCheck,
  AlertTriangle,
  RefreshCw, Search, MapPin, Clock, Check, X,
  Loader2, Building2, Radio, Users, UserPlus, UserMinus,
  Lock, LockOpen, Home, LayoutGrid, List, Pencil, Plus, Wifi, Trash2, QrCode, Siren,
} from 'lucide-react';
import { useAlarmsStore } from '@/stores/alarms-store';
import { supabase } from '@/services/supabase';
import * as alarmsService from '@/services/alarms';
import type { IntelbrasStatus } from '@/services/alarms';
import { fetchHppSyncStatus } from '@/services/alarms';
import { Pagination, usePagination } from '@/components/Pagination';
import { fetchAccounts, fetchSitesByAccount } from '@/services/accounts';
import { AdminOnboardingQR } from '@/components/accounts/AdminOnboardingQR';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ALARM_PANEL_STATUS_CONFIG,
} from '@asi/shared';
import type {
  AlarmPanelWithSite,
  AlarmPanelStatus,
} from '@asi/shared';
import { cn } from '@/lib/utils';

const STATUS_FILTERS: Array<AlarmPanelStatus | 'ALL'> = ['ALL', 'ONLINE', 'OFFLINE', 'ALARM', 'FAULT'];

function formatRelativeTime(date: string | null): string {
  if (!date) return '-';
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  
  if (diff < 60000) return 'Hace un momento';
  if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
  if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}


// ─── Panel Card Component ────────────────────────────────────

function ArmBadge({ isArmed, armMode }: { isArmed: boolean; armMode: string | null }) {
  if (!isArmed) {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
        <LockOpen className="w-3 h-3" />
        Desarmado
      </span>
    );
  }
  const isStay = armMode === 'stay';
  return (
    <span className={cn(
      'inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full',
      isStay
        ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    )}>
      {isStay ? <Home className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
      {isStay ? 'Armado en casa' : 'Armado total'}
    </span>
  );
}

type SubsystemSummary = { id: number; is_armed: boolean; arm_mode: string | null; name?: string };

/** Shows arm state per subsystem when there are multiple areas with different states. */
function ArmStatus({ panel }: { panel: AlarmPanelWithSite }) {
  const subs: SubsystemSummary[] | null =
    Array.isArray(panel.subsystems) && (panel.subsystems as SubsystemSummary[]).length > 1
      ? (panel.subsystems as SubsystemSummary[])
      : null;

  // Single area or no subsystems data — fall back to simple badge
  if (!subs) {
    return <ArmBadge isArmed={panel.is_armed} armMode={panel.arm_mode} />;
  }

  const armedSubs = subs.filter((s) => s.is_armed);

  // All disarmed
  if (armedSubs.length === 0) {
    return <ArmBadge isArmed={false} armMode={null} />;
  }

  // All armed with the same mode → single summary badge
  if (armedSubs.length === subs.length) {
    const allStay = armedSubs.every((s) => s.arm_mode === 'stay');
    const allAway = armedSubs.every((s) => s.arm_mode === 'away');
    if (allStay || allAway) {
      return <ArmBadge isArmed={true} armMode={allStay ? 'stay' : 'away'} />;
    }
  }

  // Mixed (some armed, some not, or different modes) → per-area pills
  return (
    <div className="flex flex-wrap gap-1">
      {subs.map((sub) => {
        const label = sub.name ?? `Área ${sub.id}`;
        if (!sub.is_armed) {
          return (
            <span
              key={sub.id}
              className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500"
            >
              <LockOpen className="w-2.5 h-2.5" />
              {label}
            </span>
          );
        }
        const isStay = sub.arm_mode === 'stay';
        return (
          <span
            key={sub.id}
            className={cn(
              'inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full',
              isStay
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
            )}
          >
            {isStay ? <Home className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
            {label}
          </span>
        );
      })}
    </div>
  );
}

function PanelCard({
  panel,
  isSelected,
  onClick,
}: {
  panel: AlarmPanelWithSite;
  isSelected: boolean;
  onClick: () => void;
}) {
  const statusConfig = ALARM_PANEL_STATUS_CONFIG[panel.status];
  const StatusIcon = panel.status === 'ONLINE' ? ShieldCheck
    : panel.status === 'ALARM' ? ShieldAlert
    : panel.status === 'FAULT' ? AlertTriangle
    : ShieldOff;

  return (
    <div
      onClick={onClick}
      className={cn(
        'p-4 rounded-lg border cursor-pointer transition-all',
        'hover:border-primary/50 hover:shadow-sm',
        isSelected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border bg-card'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="p-2 rounded-lg flex-shrink-0"
            style={{ backgroundColor: statusConfig.bgColor }}
          >
            <StatusIcon className="w-5 h-5" style={{ color: statusConfig.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-foreground truncate">{panel.name}</h3>
            <p className="text-sm text-muted-foreground truncate">
              {panel.serial_number} • {panel.model ?? 'Panel'}
            </p>
            {panel.site && (
              <div className="mt-1 space-y-0.5">
                <p className="text-xs text-muted-foreground/70 flex items-center gap-1">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  {panel.site.name}
                </p>
                {panel.site.account && (
                  <p className="text-xs text-muted-foreground/50 flex items-center gap-1">
                    <Building2 className="w-3 h-3 flex-shrink-0" />
                    {panel.site.account.name}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <Badge
          variant="outline"
          className="flex-shrink-0 text-xs"
          style={{
            borderColor: statusConfig.color,
            color: statusConfig.color,
            backgroundColor: statusConfig.bgColor,
          }}
        >
          {statusConfig.label}
        </Badge>
      </div>
      
      <div className="mt-2 flex items-center justify-between">
        <ArmStatus panel={panel} />
        {panel.last_heartbeat_at && (
          <p className="text-xs text-muted-foreground/60 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatRelativeTime(panel.last_heartbeat_at)}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Event Row Component ─────────────────────────────────────


// ─── Site → Client Assignments ───────────────────────────────

function SiteClientAssignments({ siteId }: { siteId: string }) {
  const [assignments, setAssignments] = useState<Array<{ client_id: string; client: { id: string; full_name: string; email: string } }>>([]);
  const [allClients, setAllClients] = useState<Array<{ id: string; full_name: string; email: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: assigned }, { data: clients }] = await Promise.all([
        supabase
          .from('client_sites')
          .select('client_id, client:profiles!client_id(id, full_name, email)')
          .eq('site_id', siteId),
        supabase
          .from('profiles')
          .select('id, full_name, email')
          .eq('role', 'client')
          .eq('is_active', true)
          .order('full_name'),
      ]);
      setAssignments((assigned ?? []) as unknown as typeof assignments);
      setAllClients((clients ?? []) as typeof allClients);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { loadData(); }, [loadData]);

  const assignedIds = new Set(assignments.map((a) => a.client_id));
  const unassigned = allClients.filter((c) => !assignedIds.has(c.id));

  const handleAssign = async (clientId: string) => {
    setAdding(true);
    try {
      await supabase.from('client_sites').insert({ client_id: clientId, site_id: siteId });
      await loadData();
      setShowAdd(false);
    } finally {
      setAdding(false);
    }
  };

  const handleUnassign = async (clientId: string) => {
    await supabase.from('client_sites').delete().eq('client_id', clientId).eq('site_id', siteId);
    await loadData();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-muted-foreground text-xs uppercase tracking-wider flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          Clientes asignados
        </p>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title="Asignar cliente"
        >
          <UserPlus className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : assignments.length === 0 ? (
        <p className="text-muted-foreground/60 text-xs py-2">
          Sin clientes asignados. Este sitio no es visible para ningún cliente.
        </p>
      ) : (
        <div className="space-y-1.5">
          {assignments.map((a) => (
            <div
              key={a.client_id}
              className="flex items-center justify-between px-2 py-1.5 rounded-md bg-muted/50 group"
            >
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{a.client.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{a.client.email}</p>
              </div>
              <button
                onClick={() => handleUnassign(a.client_id)}
                className="text-muted-foreground/40 hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 shrink-0 ml-2"
                title="Desasignar"
              >
                <UserMinus className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showAdd && unassigned.length > 0 && (
        <div className="mt-2 border border-border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
          {unassigned.map((c) => (
            <button
              key={c.id}
              onClick={() => handleAssign(c.id)}
              disabled={adding}
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors flex items-center justify-between disabled:opacity-50"
            >
              <div className="min-w-0">
                <p className="text-foreground truncate">{c.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.email}</p>
              </div>
              <UserPlus className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-2" />
            </button>
          ))}
        </div>
      )}
      {showAdd && unassigned.length === 0 && !loading && (
        <p className="text-muted-foreground/60 text-xs mt-2">
          Todos los clientes ya están asignados.
        </p>
      )}
    </div>
  );
}

// ─── Panel Detail Component ──────────────────────────────────

function PanelDetail({
  panel,
  onClose,
  onRefresh,
  onDeleted,
}: {
  panel: AlarmPanelWithSite;
  onClose: () => void;
  onRefresh: () => void;
  onDeleted: () => void;
}) {
  const statusConfig = ALARM_PANEL_STATUS_CONFIG[panel.status];
  const [arming, setArming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [loadingSubsystems, setLoadingSubsystems] = useState(false);
  // Local subsystem state (refreshed from ISAPI on demand)
  const [subsystems, setSubsystems] = useState<{ id: number; is_armed: boolean; arm_mode: string | null; name?: string }[]>(
    Array.isArray(panel.subsystems) && panel.subsystems.length > 0
      ? (panel.subsystems as { id: number; is_armed: boolean; arm_mode: string | null; name?: string }[])
      : [{ id: 1, is_armed: panel.is_armed, arm_mode: panel.arm_mode }],
  );
  const multiArea = subsystems.length > 1;

  // Sync subsystem state when the panel prop is updated via Realtime.
  // Preserve custom name edits in progress and names saved by the user.
  useEffect(() => {
    const incoming = Array.isArray(panel.subsystems) && panel.subsystems.length > 0
      ? (panel.subsystems as { id: number; is_armed: boolean; arm_mode: string | null; name?: string }[])
      : [{ id: 1, is_armed: panel.is_armed, arm_mode: panel.arm_mode }];
    setSubsystems((prev) =>
      incoming.map((s) => ({
        ...s,
        // Keep the user's custom name if already set locally
        name: prev.find((p) => p.id === s.id)?.name ?? s.name,
      })),
    );
  }, [panel.status, panel.is_armed, panel.arm_mode, panel.subsystems]);

  // Name-edit state: { [subsysId]: draftName }
  const [editingNames, setEditingNames] = useState<Record<number, string>>({});
  const [savingNames, setSavingNames] = useState(false);

  const refreshSubsystems = useCallback(async () => {
    setLoadingSubsystems(true);
    try {
      const fresh = await alarmsService.getSubsystems(panel.serial_number);
      if (fresh.length > 0) {
        // Preserve custom names from current state via functional updater (no stale closure)
        setSubsystems((prev) => fresh.map((s) => ({
          ...s,
          name: prev.find((c) => c.id === s.id)?.name ?? s.name,
        })));
      }
    } finally {
      setLoadingSubsystems(false);
    }
  }, [panel.serial_number]);

  // Auto-refresh subsystems from ISAPI when the drawer opens (online panels only).
  // Uses a one-shot ref so it doesn't fire again on subsequent panel prop updates.
  const hasAutoRefreshed = useRef(false);
  useEffect(() => {
    if (!hasAutoRefreshed.current && (panel.status === 'ONLINE' || panel.status === 'ALARM')) {
      hasAutoRefreshed.current = true;
      refreshSubsystems();
    }
  }, [panel.status, refreshSubsystems]);

  const [armError, setArmError] = useState<string | null>(null);
  const [armErrorSubsysId, setArmErrorSubsysId] = useState<number | null>(null);
  const [bypassBusy, setBypassBusy] = useState<string | null>(null);

  const handleArm = async (subsysId: number, mode: 'away' | 'stay') => {
    const key = `${subsysId}:arm`;
    setArming(key);
    setArmError(null);
    setArmErrorSubsysId(null);
    try {
      await alarmsService.armSubsystem(panel.serial_number, subsysId, mode);
      setSubsystems((prev) => prev.map((s) => s.id === subsysId ? { ...s, is_armed: true, arm_mode: mode } : s));
      onRefresh();
    } catch (e) {
      setArmError((e as Error).message ?? 'Error al armar');
      setArmErrorSubsysId(subsysId);
    } finally {
      setArming(null);
    }
  };

  const handleArmWithBypass = async (subsysId: number, mode: 'away' | 'stay') => {
    const key = `${subsysId}:bypass`;
    setBypassBusy(key);
    setArmError(null);
    setArmErrorSubsysId(null);
    try {
      await alarmsService.armSubsystem(panel.serial_number, subsysId, mode, true);
      setSubsystems((prev) => prev.map((s) => s.id === subsysId ? { ...s, is_armed: true, arm_mode: mode } : s));
      onRefresh();
    } catch (e) {
      setArmError((e as Error).message ?? 'Error al armar con bypass');
      setArmErrorSubsysId(subsysId);
    } finally {
      setBypassBusy(null);
    }
  };

  const handleDisarm = async (subsysId: number) => {
    const key = `${subsysId}:disarm`;
    setArming(key);
    setArmError(null);
    setArmErrorSubsysId(null);
    try {
      await alarmsService.disarmSubsystem(panel.serial_number, subsysId);
      setSubsystems((prev) => prev.map((s) => s.id === subsysId ? { ...s, is_armed: false, arm_mode: null } : s));
      onRefresh();
    } catch (e) {
      setArmError((e as Error).message ?? 'Error al desarmar');
      setArmErrorSubsysId(subsysId);
    } finally {
      setArming(null);
    }
  };

  // Audible panic (panel siren), the same action as the SOS button of the client app.
  const [panicBusy, setPanicBusy] = useState(false);
  const [panicMsg, setPanicMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const handlePanic = async () => {
    if (!window.confirm(`¿Activar la alarma de pánico (sirena) de "${panel.name}"?

Suena la sirena del panel y llega como alarma de pánico a monitoreo y a los usuarios de la cuenta.`)) return;
    setPanicBusy(true);
    setPanicMsg(null);
    try {
      await alarmsService.triggerPanic(panel.serial_number);
      setPanicMsg({ ok: true, text: 'Alarma de pánico activada. Para silenciar la sirena, desarma el panel.' });
    } catch (e) {
      setPanicMsg({ ok: false, text: (e as Error).message ?? 'No se pudo activar la alarma de pánico' });
    } finally {
      setPanicBusy(false);
    }
  };

  const handleSaveNames = async () => {
    if (Object.keys(editingNames).length === 0) return;
    setSavingNames(true);
    try {
      await alarmsService.saveSubsystemNames(panel.id, editingNames);
      setSubsystems((prev) => prev.map((s) => ({
        ...s,
        name: editingNames[s.id] !== undefined ? editingNames[s.id] : s.name,
      })));
      setEditingNames({});
    } finally {
      setSavingNames(false);
    }
  };

  const isOnline = panel.status === 'ONLINE' || panel.status === 'ALARM';

  const meta = (panel.metadata ?? {}) as Record<string, unknown>;
  const isIntelbras = (meta.brand as string) === 'intelbras';
  // Cloud control (no VPN): uses cloud_mac + cloud_password
  const hasCloudControl = isIntelbras && !!(meta.cloud_mac as string | undefined);
  // Direct TCP (VPN): uses local_ip + control_password
  const hasIntelbrasControl = isIntelbras && (!hasCloudControl) && !!(meta.local_ip as string | undefined);
  // Use cloud preferentially; fall back to direct TCP
  const hasAnyControl = hasCloudControl || hasIntelbrasControl;

  const [tcpStatus, setTcpStatus] = useState<IntelbrasStatus | null>(null);
  const [tcpLoading, setTcpLoading] = useState(false);
  const [tcpBusy, setTcpBusy] = useState<'arm' | 'disarm' | null>(null);
  const [tcpError, setTcpError] = useState<string | null>(null);

  const refreshTcpStatus = useCallback(async () => {
    if (!hasAnyControl) return;
    setTcpLoading(true);
    setTcpError(null);
    try {
      const s = hasCloudControl
        ? await alarmsService.getIntelbrasCloudStatus(panel.id)
        : await alarmsService.getIntelbrasStatus(panel.id);
      setTcpStatus(s);
    } catch (e) {
      setTcpError((e as Error).message);
    } finally {
      setTcpLoading(false);
    }
  }, [panel.id, hasAnyControl, hasCloudControl]);

  useEffect(() => {
    if (hasAnyControl) refreshTcpStatus();
  }, [hasAnyControl, refreshTcpStatus]);

  const handleTcpArm = async () => {
    setTcpBusy('arm');
    setTcpError(null);
    try {
      const r = hasCloudControl
        ? await alarmsService.armIntelbrasCloudPanel(panel.id)
        : await alarmsService.armIntelbrasPanel(panel.id);
      if (r.status) setTcpStatus(r.status);
      if (!r.success) setTcpError(r.error ?? 'No se pudo activar');
      else onRefresh();
    } catch (e) {
      setTcpError((e as Error).message);
    } finally {
      setTcpBusy(null);
    }
  };

  const handleTcpDisarm = async () => {
    setTcpBusy('disarm');
    setTcpError(null);
    try {
      const r = hasCloudControl
        ? await alarmsService.disarmIntelbrasCloudPanel(panel.id)
        : await alarmsService.disarmIntelbrasPanel(panel.id);
      if (r.status) setTcpStatus(r.status);
      if (!r.success) setTcpError(r.error ?? 'No se pudo desactivar');
      else onRefresh();
    } catch (e) {
      setTcpError((e as Error).message);
    } finally {
      setTcpBusy(null);
    }
  };

  return (
    <div className="w-[420px] border-l bg-card flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold text-foreground">Detalle del Panel</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10"
            disabled={deleting}
            onClick={async () => {
              if (!window.confirm(`¿Eliminar el panel "${panel.name}"?\n\nSe eliminarán también todos sus eventos y zonas.`)) return;
              setDeleting(true);
              const { error } = await supabase.from('alarm_panels').delete().eq('id', panel.id);
              setDeleting(false);
              if (error) { alert('Error al eliminar: ' + error.message); return; }
              onDeleted();
            }}
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* ── Areas / Subsystems ──────────────────────────────── */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {multiArea ? 'Áreas del panel' : 'Control de armado'}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshSubsystems}
                disabled={loadingSubsystems || !isOnline}
                className="h-7 px-2 gap-1 text-xs"
              >
                {loadingSubsystems
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <RefreshCw className="w-3 h-3" />}
                Actualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {subsystems.map((sub) => {
              const subBusy = arming?.startsWith(`${sub.id}:`);
              const displayName = editingNames[sub.id] !== undefined
                ? editingNames[sub.id]
                : (sub.name ?? (multiArea ? `Área ${sub.id}` : 'Armado'));
              const isEditing = editingNames[sub.id] !== undefined;

              return (
                <div key={sub.id} className="rounded-lg border p-3 space-y-2">
                  {/* Area header: name + edit */}
                  <div className="flex items-center gap-2">
                    {isEditing ? (
                      <Input
                        className="h-7 text-sm flex-1"
                        value={editingNames[sub.id]}
                        onChange={(e) => setEditingNames((prev) => ({ ...prev, [sub.id]: e.target.value }))}
                        placeholder={`Área ${sub.id}`}
                        autoFocus
                      />
                    ) : (
                      <span className="text-sm font-medium flex-1">{displayName}</span>
                    )}
                    <button
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() =>
                        setEditingNames((prev) =>
                          isEditing
                            ? (() => { const n = { ...prev }; delete n[sub.id]; return n; })()
                            : { ...prev, [sub.id]: sub.name ?? '' },
                        )
                      }
                    >
                      {isEditing
                        ? <X className="w-3.5 h-3.5" />
                        : <Pencil className="w-3.5 h-3.5" />}
                    </button>
                    {/* Arm state badge */}
                    <Badge
                      variant="outline"
                      className="text-xs flex-shrink-0"
                      style={{
                        borderColor: sub.is_armed ? '#22c55e' : '#6b7280',
                        color: sub.is_armed ? '#22c55e' : '#6b7280',
                      }}
                    >
                      {sub.is_armed
                        ? (sub.arm_mode === 'stay' ? 'Perimetral' : 'Total')
                        : 'Desarmado'}
                    </Badge>
                  </div>

                  {/* Arm/disarm buttons — hidden for Intelbras TCP panels (use control card instead) */}
                  {!hasAnyControl && <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArm(sub.id, 'away')}
                        disabled={subBusy || !isOnline || (sub.is_armed && sub.arm_mode === 'away')}
                        className="flex-col h-auto py-2 text-xs"
                      >
                        {subBusy && arming === `${sub.id}:arm`
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <ShieldCheck className="w-3.5 h-3.5" />}
                        Total
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleArm(sub.id, 'stay')}
                        disabled={subBusy || !isOnline || (sub.is_armed && sub.arm_mode === 'stay')}
                        className="flex-col h-auto py-2 text-xs"
                      >
                        {subBusy && arming === `${sub.id}:arm`
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Shield className="w-3.5 h-3.5" />}
                        Parcial
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDisarm(sub.id)}
                        disabled={subBusy || !isOnline || !sub.is_armed}
                        className="flex-col h-auto py-2 text-xs"
                      >
                        {subBusy && arming === `${sub.id}:disarm`
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <ShieldOff className="w-3.5 h-3.5" />}
                        Desarmar
                      </Button>
                    </div>

                    {/* Bypass section: only for a panel-level trouble like a dead/missing
                        battery ("¿Deseas armar de todas formas?") — an open zone (door/window)
                        has no safe software bypass and is never offered one here; it always
                        falls through to the plain-error block below instead. */}
                    {armError && sub.id === armErrorSubsysId && !sub.is_armed && armError.toLowerCase().includes('todas formas') && (
                      <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-2.5 space-y-2">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-amber-800 dark:text-amber-400">{armError}</p>
                        </div>
                        <p className="text-xs text-amber-700 dark:text-amber-500 ml-6">
                          El panel tiene una falla técnica activa pero puedes forzar el armado igualmente.
                        </p>
                        <div className="space-y-1.5 ml-6">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleArmWithBypass(sub.id, 'away')}
                            disabled={!!bypassBusy}
                            className="w-full justify-start text-xs border-amber-400 text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-950"
                          >
                            {bypassBusy === `${sub.id}:bypass`
                              ? <Loader2 className="w-3 h-3 animate-spin mr-2" />
                              : <ShieldCheck className="w-3 h-3 mr-2" />}
                            Armar de todas formas (total)
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleArmWithBypass(sub.id, 'stay')}
                            disabled={!!bypassBusy}
                            className="w-full justify-start text-xs border-amber-400 text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-950"
                          >
                            {bypassBusy === `${sub.id}:bypass`
                              ? <Loader2 className="w-3 h-3 animate-spin mr-2" />
                              : <Shield className="w-3 h-3 mr-2" />}
                            Armar de todas formas (parcial)
                          </Button>
                        </div>
                      </div>
                    )}

                    {armError && sub.id === armErrorSubsysId && !armError.toLowerCase().includes('todas formas') && (
                      armError.toLowerCase().includes('tiempo de salida') || armError.toLowerCase().includes('proceso de armado')
                        ? (
                          <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-2 flex items-start gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-800 dark:text-amber-400">{armError}</p>
                          </div>
                        ) : (
                          <p className="text-xs text-destructive flex items-center gap-1">
                            <X className="w-3 h-3" />{armError}
                          </p>
                        )
                    )}
                  </div>}
                </div>
              );
            })}

            {/* Save names button — only shown when there are pending edits */}
            {Object.keys(editingNames).length > 0 && (
              <Button
                size="sm"
                className="w-full gap-2"
                onClick={handleSaveNames}
                disabled={savingNames}
              >
                {savingNames ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Guardar nombres de áreas
              </Button>
            )}

            {/* Panic: Hikvision (HPP) panels only — Intelbras has no one-key alarm in the gateway. */}
            {!isIntelbras && (
              <div className="space-y-1.5 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePanic}
                  disabled={panicBusy || !isOnline}
                  className="w-full gap-2 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  {panicBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Siren className="w-4 h-4" />}
                  Alarma de pánico (sirena)
                </Button>
                {panicMsg && (
                  <p className={cn('text-xs', panicMsg.ok ? 'text-muted-foreground' : 'text-destructive')}>
                    {panicMsg.text}
                  </p>
                )}
              </div>
            )}

            {!isOnline && (
              <p className="text-xs text-muted-foreground text-center">
                El panel debe estar en línea para controlarlo
              </p>
            )}
          </CardContent>
        </Card>

        {/* Panel info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {panel.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Serial</p>
                <p className="font-mono text-xs">{panel.serial_number}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Modelo</p>
                <p>{panel.model ?? '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Firmware</p>
                <p>{panel.firmware_version ?? '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Estado</p>
                <Badge
                  variant="outline"
                  style={{
                    borderColor: statusConfig.color,
                    color: statusConfig.color,
                    backgroundColor: statusConfig.bgColor,
                  }}
                >
                  {statusConfig.label}
                </Badge>
              </div>
            </div>

            {panel.site && (
              <div className="pt-2 border-t">
                <p className="text-muted-foreground text-sm mb-1">Sitio</p>
                <p className="font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {panel.site.name}
                </p>
                {panel.site.address && (
                  <p className="text-sm text-muted-foreground ml-6">{panel.site.address}</p>
                )}
                {!panel.site.account_id && (
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => setShowQrModal(true)}>
                    <QrCode className="w-4 h-4 mr-1" />
                    Generar QR de onboarding
                  </Button>
                )}
              </div>
            )}

            {showQrModal && panel.site && (
              <Dialog open onOpenChange={(open) => !open && setShowQrModal(false)}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>QR de onboarding — {panel.site.name}</DialogTitle>
                    <DialogDescription>
                      Este sitio aún no pertenece a ninguna cuenta. El cliente que escanee este código
                      quedará como dueño de una cuenta nueva (o se vinculará a su cuenta existente si ya
                      tiene una de solo app).
                    </DialogDescription>
                  </DialogHeader>
                  <AdminOnboardingQR accountId="" siteId={panel.site.id} siteName={panel.site.name} />
                </DialogContent>
              </Dialog>
            )}
          </CardContent>
        </Card>

        {/* Client Assignments */}
        {panel.site_id && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Acceso de clientes</CardTitle>
            </CardHeader>
            <CardContent>
              <SiteClientAssignments siteId={panel.site_id} />
            </CardContent>
          </Card>
        )}

        {/* ── Intelbras TCP Direct Control ──────────────────── */}
        {hasAnyControl && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wifi className="w-4 h-4 text-blue-500" />
                  Control remoto
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={refreshTcpStatus} disabled={tcpLoading}
                  className="h-7 px-2 gap-1 text-xs">
                  {tcpLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Status */}
              {tcpStatus && (
                <div className="rounded-lg bg-muted/40 border divide-y text-sm">
                  {[
                    ['Conexión', tcpStatus.connected ? '✅ Conectado' : '❌ Sin conexión'],
                    ['Estado', tcpStatus.armed ? '🔴 Activado' : '🟢 Desactivado'],
                    ...(tcpStatus.alarm ? [['Alarma', '🚨 Siren activa']] : []),
                    ...(tcpStatus.alarm_memory ? [['Memoria', '⚠️ Evento registrado']] : []),
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between px-3 py-1.5 text-xs">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium">{val}</span>
                    </div>
                  ))}
                </div>
              )}

              {tcpError && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <X className="w-3 h-3" />{tcpError}
                </p>
              )}

              {/* Control buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={handleTcpArm}
                  disabled={!!tcpBusy || tcpStatus?.armed === true}
                  className="flex-col h-auto py-2.5 gap-1 text-xs">
                  {tcpBusy === 'arm' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4 text-green-600" />}
                  Activar
                </Button>
                <Button variant="outline" size="sm" onClick={handleTcpDisarm}
                  disabled={!!tcpBusy || tcpStatus?.armed === false}
                  className="flex-col h-auto py-2.5 gap-1 text-xs">
                  {tcpBusy === 'disarm' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4 text-slate-500" />}
                  Desactivar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {hasCloudControl ? `Cloud relay 0xe7 · ${meta.cloud_mac as string}` : `TCP directo 0xe7 · ${meta.local_ip as string}`}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── Stats Cards ─────────────────────────────────────────────

function StatsCards({ panels }: { panels: AlarmPanelWithSite[] }) {
  const online = panels.filter((p) => p.status === 'ONLINE').length;
  const offline = panels.filter((p) => p.status === 'OFFLINE').length;
  const inAlarm = panels.filter((p) => p.status === 'ALARM').length;
  const armed = panels.filter((p) => p.is_armed).length;
  const unassigned = panels.filter((p) => !p.site || !p.site.account_id).length;

  const card = (label: string, value: number, icon: ReactNode, tone: string, hint?: ReactNode) => (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold', tone)}>{value}</p>
          </div>
          {icon}
        </div>
        {hint && <div className="flex gap-2 mt-2 text-xs">{hint}</div>}
      </CardContent>
    </Card>
  );

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {card('Paneles', panels.length,
        <div className="p-2 rounded-lg bg-green-500/10"><Radio className="w-5 h-5 text-green-600" /></div>, '',
        <>
          <span className="text-green-600">{online} en línea</span>
          <span className="text-muted-foreground">•</span>
          <span className="text-muted-foreground">{offline} fuera de línea</span>
        </>)}
      {card('En alarma', inAlarm,
        <div className="p-2 rounded-lg bg-red-500/10"><ShieldAlert className="w-5 h-5 text-red-600" /></div>, 'text-red-600')}
      {card('Armados', armed,
        <div className="p-2 rounded-lg bg-blue-500/10"><Shield className="w-5 h-5 text-blue-600" /></div>, '',
        <span className="text-muted-foreground">{panels.length - armed} desarmados</span>)}
      {card('Sin cuenta', unassigned,
        <div className="p-2 rounded-lg bg-amber-500/10"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>, 'text-amber-600',
        <span className="text-muted-foreground">pendientes de asignar a un cliente</span>)}
    </div>
  );
}

// ─── TCP Panel Registration Dialog ──────────────────────────────
// Generic across brands — each brand maps to a fixed port/protocol.

const PANEL_BRANDS: Array<{
  id: string;
  label: string;
  port: number;
  protocol: string;
  models: string[];
}> = [
  {
    id: 'intelbras',
    label: 'Intelbras',
    port: 3010,
    protocol: 'CONTACT_ID',
    models: ['AMT 2018', 'AMT 2018 E', 'AMT 8000', 'AMT 8000 Pro', 'ST-IIIIP', 'XAT 3000 Smart', 'Otro'],
  },
  {
    id: 'hikvision',
    label: 'Hikvision',
    port: 3020,
    protocol: 'CONTACT_ID',
    models: ['AX PRO (DS-PHA48/64)', 'AX HOME', 'Hybrid Pro', 'DS-19A Series', 'Otro'],
  },
  {
    id: 'dsc',
    label: 'DSC',
    port: 3010,
    protocol: 'CONTACT_ID',
    models: ['PowerSeries', 'PC1616', 'PC1832', 'PC1864', 'Otro'],
  },
  {
    id: 'paradox',
    label: 'Paradox',
    port: 3010,
    protocol: 'CONTACT_ID',
    models: ['MG5050', 'SP4000', 'EVO48', 'Otro'],
  },
  {
    id: 'generico',
    label: 'Genérico / Otro',
    port: 3010,
    protocol: 'CONTACT_ID',
    models: ['Otro'],
  },
];

/** Find next globally-unique TCP receiver account code (4 hex digits, 1001–FFFF). */
async function nextTcpAccountCode(): Promise<string> {
  const { data } = await supabase
    .from('alarm_panels')
    .select('serial_number')
    .ilike('metadata->>receiver', 'alarmas.corporacionasi.com');
  const used = new Set((data ?? []).map((r: { serial_number: string }) => r.serial_number.toUpperCase()));
  for (let i = 1001; i <= 0xFFFF; i++) {
    const code = i.toString(16).toUpperCase().padStart(4, '0');
    if (!used.has(code)) return code;
  }
  return Date.now().toString(16).toUpperCase().slice(-4);
}

function RegisterTcpPanelDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1: brand + model
  const [brandId, setBrandId] = useState(PANEL_BRANDS[0].id);
  const [model, setModel] = useState('');
  const [panelName, setPanelName] = useState('');

  // Step 2: account + site
  const [accounts, setAccounts] = useState<Array<{ id: string; name: string; account_number: string }>>([]);
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [accountId, setAccountId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountType, setNewAccountType] = useState('residential');
  const [savingAccount, setSavingAccount] = useState(false);

  // Step 3: confirm + code
  const [accountCode, setAccountCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Optional control config (Intelbras only)
  const [localIp, setLocalIp] = useState('');
  const [controlPassword, setControlPassword] = useState('');
  const [panelModel, setPanelModel] = useState<'AMT1016NET' | 'AMT2018EG'>('AMT1016NET');
  const [cloudMac, setCloudMac] = useState('');
  const [cloudPassword, setCloudPassword] = useState('');

  const brand = PANEL_BRANDS.find(b => b.id === brandId) ?? PANEL_BRANDS[0];

  // Load accounts on open
  useEffect(() => {
    if (!open) { setStep(1); setBrandId(PANEL_BRANDS[0].id); setModel(''); setPanelName(''); setAccountId(''); setSiteId(''); setAccountCode(''); setLocalIp(''); setControlPassword(''); setPanelModel('AMT1016NET'); setCloudMac(''); setCloudPassword(''); setError(''); return; }
    fetchAccounts()
      .then(a => setAccounts(a.map(x => ({ id: x.id, name: x.name, account_number: x.account_number }))))
      .catch(() => {});
  }, [open]);

  // Load sites when account changes
  useEffect(() => {
    if (!accountId) { setSites([]); setSiteId(''); return; }
    fetchSitesByAccount(accountId)
      .then(s => setSites(s.map(x => ({ id: x.id, name: x.name }))))
      .catch(() => {});
  }, [accountId]);

  // Generate code when reaching step 3
  useEffect(() => {
    if (step === 3 && !accountCode) {
      nextTcpAccountCode().then(setAccountCode);
    }
  }, [step]);

  const handleCreateAccount = async () => {
    if (!newAccountName.trim()) return;
    setSavingAccount(true);
    try {
      const { data, error: err } = await supabase.from('accounts').insert({
        name: newAccountName.trim(),
        type: newAccountType,
        is_managed: true,
        feature_cameras: false,
        feature_patrols: false,
      }).select('id, name, account_number').single();
      if (err) throw new Error(err.message);
      const created = data as { id: string; name: string; account_number: string };
      setAccounts(prev => [...prev, created]);
      setAccountId(created.id);
      setCreatingAccount(false);
      setNewAccountName('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingAccount(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { error: dbErr } = await supabase.from('alarm_panels').insert({
        serial_number: accountCode,
        name: panelName.trim(),
        model: `${brand.label}${model ? ' ' + model : ''}`,
        site_id: siteId,
        status: 'OFFLINE',
        hpp_device_id: '',
        is_armed: false,
        is_subscribed: false,
        metadata: {
          brand: brandId,
          protocol: brand.protocol,
          port: brand.port,
          receiver: 'alarmas.corporacionasi.com',
          ...(brandId === 'intelbras' && cloudMac.trim() ? {
            cloud_mac: cloudMac.trim().toLowerCase().replace(/[^0-9a-f]/g, ''),
            cloud_password: cloudPassword.trim(),
          } : {}),
          ...(brandId === 'intelbras' && localIp ? {
            local_ip: localIp.trim(),
            control_password: controlPassword.trim(),
            panel_model: panelModel,
          } : {}),
        },
      });
      if (dbErr) throw new Error(dbErr.message);
      onCreated();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const canGoStep2 = brandId && panelName.trim();
  const canGoStep3 = accountId && siteId;

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-blue-500" />
            Registrar panel de alarma
          </DialogTitle>
          {/* Step indicator */}
          <div className="flex items-center gap-2 pt-1">
            {([1,2,3] as const).map(n => (
              <div key={n} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === n ? 'bg-primary text-primary-foreground' : step > n ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                  {step > n ? '✓' : n}
                </div>
                <span className={`text-xs ${step === n ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  {n === 1 ? 'Dispositivo' : n === 2 ? 'Ubicación' : 'Confirmar'}
                </span>
                {n < 3 && <div className="w-6 h-px bg-border" />}
              </div>
            ))}
          </div>
        </DialogHeader>

        <div className="py-2 space-y-4">

          {/* ── Step 1: Brand + Model ── */}
          {step === 1 && (
            <>
              <div>
                <label className="text-sm font-medium block mb-1">Marca</label>
                <div className="grid grid-cols-2 gap-2">
                  {PANEL_BRANDS.map(b => (
                    <button key={b.id} onClick={() => setBrandId(b.id)}
                      className={`rounded-lg border p-3 text-left text-sm transition-all ${brandId === b.id ? 'border-primary bg-primary/5 font-medium' : 'border-border hover:border-muted-foreground/50'}`}>
                      <div className="font-medium">{b.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Puerto {b.port} · {b.protocol}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">
                  Modelo <span className="text-muted-foreground font-normal">(referencia para el técnico)</span>
                </label>
                <select value={model} onChange={e => setModel(e.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">— Seleccionar —</option>
                  {brand.models.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1">Nombre del sistema</label>
                <Input placeholder="Ej: Alarma Casa Principal" value={panelName} onChange={e => setPanelName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && canGoStep2 && setStep(2)} />
              </div>
            </>
          )}

          {/* ── Step 2: Account + Site ── */}
          {step === 2 && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Cuenta del cliente</label>
                  <button onClick={() => setCreatingAccount(!creatingAccount)}
                    className="text-xs text-primary hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" /> {creatingAccount ? 'Cancelar' : 'Nueva cuenta'}
                  </button>
                </div>

                {creatingAccount ? (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
                    <Input placeholder="Nombre de la cuenta" value={newAccountName} onChange={e => setNewAccountName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleCreateAccount()} autoFocus />
                    <select value={newAccountType} onChange={e => setNewAccountType(e.target.value)}
                      className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                      <option value="residential">Residencial</option>
                      <option value="commercial">Comercial</option>
                      <option value="corporate">Corporativo</option>
                    </select>
                    <Button size="sm" onClick={handleCreateAccount} disabled={savingAccount || !newAccountName.trim()} className="w-full gap-1">
                      {savingAccount ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      Crear y seleccionar
                    </Button>
                  </div>
                ) : (
                  <select value={accountId} onChange={e => { setAccountId(e.target.value); setSiteId(''); }}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">— Seleccionar cuenta —</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.account_number})</option>)}
                  </select>
                )}
              </div>

              {accountId && (
                <div>
                  <label className="text-sm font-medium block mb-1">Ubicación</label>
                  <select value={siteId} onChange={e => setSiteId(e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">— Seleccionar ubicación —</option>
                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {sites.length === 0 && <p className="text-xs text-muted-foreground mt-1">Esta cuenta no tiene ubicaciones aún.</p>}
                </div>
              )}
            </>
          )}

          {/* ── Step 3: Confirm ── */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/30 divide-y text-sm">
                {[
                  ['Sistema', panelName],
                  ['Marca / Modelo', `${brand.label}${model ? ' ' + model : ''}`],
                  ['Cuenta', accounts.find(a => a.id === accountId)?.name ?? '—'],
                  ['Ubicación', sites.find(s => s.id === siteId)?.name ?? '—'],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between px-3 py-2">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-medium">{val}</span>
                  </div>
                ))}
              </div>

              <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-4 space-y-2">
                <p className="text-sm font-semibold text-blue-700 dark:text-blue-400">Configuración para el técnico:</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <span className="text-muted-foreground">Receptor IP/DNS</span>
                  <span className="font-mono">alarmas.corporacionasi.com</span>
                  <span className="text-muted-foreground">Puerto</span>
                  <span className="font-mono">{brand.port}</span>
                  <span className="text-muted-foreground">Protocolo</span>
                  <span className="font-mono">{brand.protocol}</span>
                  <span className="text-muted-foreground">Código de cuenta</span>
                  <span className="font-mono font-bold text-blue-700 dark:text-blue-400 text-sm">
                    {accountCode || <Loader2 className="w-3 h-3 animate-spin inline" />}
                  </span>
                </div>
              </div>

              {/* Cloud control — Intelbras only, via QR/sticker on panel */}
              {brandId === 'intelbras' && (
                <details className="rounded-lg border border-dashed border-blue-500/30 p-3 text-sm" open>
                  <summary className="cursor-pointer font-medium text-blue-600 select-none">
                    Control cloud ⭐ (recomendado — sin VPN, solo internet)
                  </summary>
                  <div className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      MAC impresa en el sticker del panel o escaneable vía QR. La contraseña también está en el sticker.
                    </p>
                    <div>
                      <label className="text-xs font-medium block mb-1">MAC del panel (del QR o sticker)</label>
                      <Input placeholder="Ej: 98:e5:5b:24:e6:02 o 98e55b24e602" value={cloudMac} onChange={e => setCloudMac(e.target.value)} className="h-8 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Contraseña del panel (del sticker)</label>
                      <Input placeholder="Ej: 682799" value={cloudPassword} onChange={e => setCloudPassword(e.target.value)} className="h-8 text-sm font-mono" />
                    </div>
                  </div>
                </details>
              )}

              {/* VPN/direct control — optional fallback */}
              {brandId === 'intelbras' && (
                <details className="rounded-lg border border-dashed border-muted-foreground/30 p-3 text-sm">
                  <summary className="cursor-pointer font-medium text-muted-foreground select-none">
                    Control bidireccional (opcional)
                  </summary>
                  <div className="mt-3 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Permite activar / desactivar el panel desde el dashboard. Requiere que la IP del panel sea accesible desde el servidor (reenvío de puerto 9009 o VPN WireGuard).
                    </p>
                    <div>
                      <label className="text-xs font-medium block mb-1">IP del panel (accesible desde el servidor)</label>
                      <Input placeholder="Ej: 192.168.1.100 o IP pública" value={localIp} onChange={e => setLocalIp(e.target.value)} className="h-8 text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Contraseña del panel (hex, igual que en app AMT REMOTO)</label>
                      <Input placeholder="Ej: 1234 o 878787" value={controlPassword} onChange={e => setControlPassword(e.target.value)} className="h-8 text-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Variante de protocolo</label>
                      <select value={panelModel} onChange={e => setPanelModel(e.target.value as 'AMT1016NET' | 'AMT2018EG')}
                        className="h-8 w-full rounded-md border border-input bg-background px-3 text-sm">
                        <option value="AMT1016NET">AMT 1016 NET / 2018 NET / 2018 E (estándar)</option>
                        <option value="AMT2018EG">AMT 2018 EG (firmware 8.5)</option>
                      </select>
                    </div>
                  </div>
                </details>
              )}
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive flex items-center gap-1.5">
              <X className="w-4 h-4" /> {error}
            </p>
          )}
        </div>

        <DialogFooter className="flex-row justify-between">
          <div>
            {step > 1 && (
              <Button variant="ghost" onClick={() => setStep(s => (s - 1) as 1 | 2 | 3)} disabled={saving}>
                ← Atrás
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
            {step < 3 ? (
              <Button onClick={() => setStep(s => (s + 1) as 2 | 3)}
                disabled={step === 1 ? !canGoStep2 : !canGoStep3}>
                Siguiente →
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving || !accountCode}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Registrar panel
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page Component ─────────────────────────────────────

// ─── Main Page Component ─────────────────────────────────────

export function AlarmsPage() {
  const {
    sites,
    panels,
    selectedPanel,
    loading,
    syncing,
    filterStatus,
    search,
    fetchSites,
    fetchPanels,
    syncFromHpp,
    selectPanel,
    setFilterStatus,
    setSearch,
    armPanel,
    disarmPanel,
    subscribeToPanels,
    subscribeToSites,
    filteredPanels,
  } = useAlarmsStore();

  const [addIntelbrasOpen, setAddIntelbrasOpen] = useState(false);
  const [panelViewMode, setPanelViewMode] = useState<'card' | 'list'>('card');
  const [panelAssignTab, setPanelAssignTab] = useState<'unassigned' | 'assigned'>('unassigned');

  useEffect(() => {
    fetchSites();
    fetchPanels();

    const unsubPanels = subscribeToPanels();
    const unsubSites = subscribeToSites();

    return () => {
      unsubPanels();
      unsubSites();
    };
  }, [fetchSites, fetchPanels, subscribeToPanels, subscribeToSites]);

  // HPP sync runs automatically on the gateway; show when it last ran.
  const [syncStatus, setSyncStatus] = useState<Awaited<ReturnType<typeof fetchHppSyncStatus>> | null>(null);
  const loadSyncStatus = useCallback(() => {
    fetchHppSyncStatus().then(setSyncStatus).catch(() => setSyncStatus(null));
  }, []);
  useEffect(() => {
    loadSyncStatus();
    const t = setInterval(loadSyncStatus, 60_000);
    return () => clearInterval(t);
  }, [loadSyncStatus]);

  const handleSync = async () => {
    try {
      const result = await syncFromHpp();
      console.log(`Sincronizados: ${result.sites} sitios, ${result.panels} paneles`);
    } catch (e) {
      console.error('[handleSync]', e);
      alert(`Error al sincronizar: ${(e as Error).message}`);
    } finally {
      loadSyncStatus();
    }
  };

  const allFilteredPanels = filteredPanels();
  const unassignedCount = allFilteredPanels.filter((p) => !p.site || !p.site.account_id).length;
  const visiblePanels = allFilteredPanels.filter((p) =>
    panelAssignTab === 'unassigned' ? (!p.site || !p.site.account_id) : (p.site && p.site.account_id),
  );
  const panelPager = usePagination(visiblePanels, `${search}|${filterStatus}|${panelAssignTab}`, 25);

  const handleArmPanel = async (mode: 'away' | 'stay' | 'instant') => {
    if (!selectedPanel) return;
    await armPanel(selectedPanel.serial_number, mode);
  };

  const handleDisarmPanel = async () => {
    if (!selectedPanel) return;
    await disarmPanel(selectedPanel.serial_number);
  };

  return (
    <div className="flex h-full">
      {/* Main Content */}
      <div className={cn('flex-1 flex flex-col', selectedPanel && 'max-w-[calc(100%-400px)]')}>
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-foreground">Páneles de Alarma</h2>
            <div className="flex items-center gap-3">
              <span
                className="text-xs text-muted-foreground text-right"
                title="El gateway sincroniza sitios y paneles de Hik-Partner Pro automáticamente cada 10 minutos."
              >
                Sincronización HPP automática
                <br />
                {syncStatus?.running || syncing
                  ? 'sincronizando…'
                  : syncStatus?.lastError
                    ? <span className="text-red-500">último intento falló</span>
                    : syncStatus?.lastResult
                      ? `última: ${formatRelativeTime(syncStatus.lastResult.startedAt)}`
                      : 'aún sin ejecutar'}
              </span>
              <Button
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
                className="gap-2"
                title="Forzar una sincronización ahora"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Sincronizar ahora
              </Button>
            </div>
          </div>

          {/* Stats */}
          <StatsCards panels={panels} />
        </div>

        {/* Tabs */}
        <div className="flex-1 flex flex-col px-6">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              {(
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 ml-2"
                  onClick={() => setAddIntelbrasOpen(true)}
                >
                  <Plus className="w-4 h-4" />
                  Registrar panel
                </Button>
              )}
              {(
                <div className="flex items-center border border-border rounded-md overflow-hidden">
                  <button
                    onClick={() => setPanelViewMode('card')}
                    className={cn('p-2 transition-colors', panelViewMode === 'card' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                    title="Vista de tarjetas"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPanelViewMode('list')}
                    className={cn('p-2 transition-colors', panelViewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                    title="Vista de lista"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-[200px]"
                />
              </div>

              {(
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as AlarmPanelStatus | 'ALL')}
                  className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                >
                  {STATUS_FILTERS.map((s) => (
                    <option key={s} value={s}>
                      {s === 'ALL' ? 'Todos los estados' : ALARM_PANEL_STATUS_CONFIG[s].label}
                    </option>
                  ))}
                </select>
              )}

            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            {/* Sub-tabs: unassigned vs assigned to account */}
            <div className="flex items-center gap-1 mb-3 border-b border-border pb-3">
              <button
                onClick={() => setPanelAssignTab('unassigned')}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md font-medium transition-colors',
                  panelAssignTab === 'unassigned'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                Sin cuenta
                <span className={cn(
                  'ml-1.5 text-xs px-1.5 py-0.5 rounded-full',
                  panelAssignTab === 'unassigned' ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
                )}>
                  {unassignedCount}
                </span>
              </button>
              <button
                onClick={() => setPanelAssignTab('assigned')}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-md font-medium transition-colors',
                  panelAssignTab === 'assigned'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )}
              >
                En cuenta
                <span className={cn(
                  'ml-1.5 text-xs px-1.5 py-0.5 rounded-full',
                  panelAssignTab === 'assigned' ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
                )}>
                  {allFilteredPanels.length - unassignedCount}
                </span>
              </button>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : panelViewMode === 'card' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 overflow-auto pb-6">
                {panelPager.pageItems.map((panel) => (
                  <PanelCard
                    key={panel.id}
                    panel={panel}
                    isSelected={selectedPanel?.id === panel.id}
                    onClick={() => selectPanel(selectedPanel?.id === panel.id ? null : panel)}
                  />
                ))}
                {visiblePanels.length === 0 && (
                  <div className="col-span-full text-center py-16 text-muted-foreground">
                    <ShieldOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No se encontraron paneles</p>
                    <p className="text-sm mt-1">
                      {panels.length === 0
                        ? 'Sincroniza desde HPP para importar paneles'
                        : panelAssignTab === 'unassigned'
                          ? 'Todos los paneles ya están asignados a una cuenta'
                          : 'Ningún panel está asignado a una cuenta aún'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="overflow-auto pb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Panel</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Estado</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Armado</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Sitio</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Último heartbeat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {panelPager.pageItems.map((panel) => {
                      const statusConfig = ALARM_PANEL_STATUS_CONFIG[panel.status];
                      return (
                        <tr
                          key={panel.id}
                          className={cn(
                            'border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/40',
                            selectedPanel?.id === panel.id && 'bg-primary/5'
                          )}
                          onClick={() => selectPanel(selectedPanel?.id === panel.id ? null : panel)}
                        >
                          <td className="py-2 px-3">
                            <p className="font-medium">{panel.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{panel.serial_number}</p>
                          </td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className="text-xs" style={{ borderColor: statusConfig.color, color: statusConfig.color, backgroundColor: statusConfig.bgColor }}>
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="py-2 px-3">
                            <ArmStatus panel={panel} />
                          </td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {panel.site ? (
                              <div className="space-y-0.5">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  {panel.site.name}
                                </span>
                                {panel.site.account && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground/60">
                                    <Building2 className="w-3 h-3 flex-shrink-0" />
                                    {panel.site.account.name}
                                  </span>
                                )}
                              </div>
                            ) : '—'}
                          </td>
                          <td className="py-2 px-3 text-muted-foreground text-xs">
                            {panel.last_heartbeat_at ? formatRelativeTime(panel.last_heartbeat_at) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    {visiblePanels.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-16 text-center text-muted-foreground">
                          <ShieldOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No se encontraron paneles</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              className="pb-6"
              page={panelPager.page}
              pageCount={panelPager.pageCount}
              pageSize={panelPager.pageSize}
              total={panelPager.total}
              onPageChange={panelPager.setPage}
              onPageSizeChange={panelPager.setPageSize}
              itemLabel="paneles"
            />
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedPanel && (
        <PanelDetail
          panel={selectedPanel}
          onClose={() => selectPanel(null)}
          onRefresh={fetchPanels}
          onDeleted={() => { selectPanel(null); fetchPanels(); }}
        />
      )}

      <RegisterTcpPanelDialog
        open={addIntelbrasOpen}
        onClose={() => setAddIntelbrasOpen(false)}
        onCreated={() => { fetchPanels(); }}
      />
    </div>
  );
}
